import Vue from 'vue'
import VueRouter from 'vue-router'

// Routes
import {
  isUserLoggedIn,
  getUserData,
  getHomeRouteForLoggedInUser
} from '@/auth/utils'
import apps from './routes/apps'
import dashboard from './routes/dashboard'
import workspace from './routes/workspace'
import workspaceAdmin from './routes/workspace-admin'
import { canNavigate } from '@/libs/acl/routeProtection'
import { useAuthStore } from '@/pinia/auth'
import { useSubscriptionStore } from '@/pinia/subscription'

// Routes exempt from subscription check
const SUBSCRIPTION_EXEMPT_ROUTES = ['auth-login', 'auth-register', 'workspace', 'workspace-admin', 'my-profile', 'error-404', 'error-not-authorized']
// import pages from './routes/pages'
// import dashboard from '@/views/Dashboard.vue'

Vue.use(VueRouter)

/**
 * Handle chunk loading errors gracefully
 * When a deployment happens while users have old cached pages,
 * they may try to load chunks that no longer exist (404)
 * This detects such errors and refreshes the page to get new assets
 */
const handleChunkError = (error) => {
  const isChunkError = error && (
    error.name === 'ChunkLoadError' ||
    (error.message && error.message.includes('Loading chunk')) ||
    (error.message && error.message.includes('Loading CSS chunk')) ||
    (error.request && error.request.status === 404)
  )

  if (isChunkError) {
    console.warn('🔄 Chunk loading failed, refreshing for new assets...')

    // Store flag to prevent infinite refresh loop
    const lastRefresh = sessionStorage.getItem('chunk_error_refresh')
    const now = Date.now()

    // Only auto-refresh if we haven't done so in the last 30 seconds
    if (!lastRefresh || (now - parseInt(lastRefresh, 10)) > 30000) {
      sessionStorage.setItem('chunk_error_refresh', now.toString())

      // Clear caches before refresh
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => caches.delete(name))
        })
      }

      // Clear service worker cache
      if (navigator.serviceWorker && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' })
      }

      // Hard refresh to bypass cache
      window.location.reload(true)
      return true
    }
  }

  return false
}

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    ...apps,
    ...dashboard,
    ...workspace,
    ...workspaceAdmin,
    // ...pages,
    {
      path: '*',
      redirect: 'error-404'
    }
  ]
})

// Suppress NavigationDuplicated / NavigationRedirected errors
// These are expected when guards redirect (e.g. expired token → /login)
const originalPush = router.push.bind(router)
router.push = (location, onResolve, onReject) => {
  if (onResolve || onReject) return originalPush(location, onResolve, onReject)
  return originalPush(location).catch(err => {
    if (VueRouter.isNavigationFailure(err)) return err
    throw err
  })
}

const originalReplace = router.replace.bind(router)
router.replace = (location, onResolve, onReject) => {
  if (onResolve || onReject) return originalReplace(location, onResolve, onReject)
  return originalReplace(location).catch(err => {
    if (VueRouter.isNavigationFailure(err)) return err
    throw err
  })
}

router.beforeEach((to, from, next) => {
  const isLoggedIn = isUserLoggedIn()

  // Check if route has alwaysAccessible meta
  const hasAlwaysAccessible = to.matched.some(route => route.meta.alwaysAccessible)

  // Handle redirectIfLoggedIn early for alwaysAccessible routes
  if (hasAlwaysAccessible && to.meta.redirectIfLoggedIn && isLoggedIn) {
    const userData = getUserData()
    return next(getHomeRouteForLoggedInUser(userData ? userData.role : null))
  }

  // Allow alwaysAccessible routes to pass through
  if (hasAlwaysAccessible) {
    if (to.meta.keepAlive === undefined) {
      to.meta.keepAlive = true
    }
    return next()
  }

  // Check permissions for non-alwaysAccessible routes
  if (!canNavigate(to)) {
    // Redirect to login if not logged in
    if (!isLoggedIn) return next({ name: 'auth-login' })

    // User is logged in but permissions may not be loaded yet (e.g. after hard refresh).
    // Allow navigation — the backend enforces permissions on API calls,
    // and the menu is already filtered by hasPermission.
    // Redirecting to login here causes an infinite loop because
    // auth-login redirects logged-in users back, which gets silently aborted.
    return next()
  }

  if (to.meta.keepAlive === undefined) {
    to.meta.keepAlive = true
  }

  // Redirect if logged in (for non-alwaysAccessible routes)
  if (to.meta.redirectIfLoggedIn && isLoggedIn) {
    const userData = getUserData()
    return next(getHomeRouteForLoggedInUser(userData ? userData.role : null))
  }

  // Subscription lockdown check for logged-in users
  if (isLoggedIn && to.name && !SUBSCRIPTION_EXEMPT_ROUTES.includes(to.name)) {
    const authStore = useAuthStore()
    if (authStore.isSubscriptionLocked) {
      // Show toast notification so user knows why they're being redirected
      if (router.app && router.app.$dekatoast) {
        router.app.$dekatoast.warning(
          'Aktifkan langganan terlebih dahulu untuk mengakses fitur ini.'
        )
      }
      return next({ name: 'workspace', query: { tab: 'berlangganan' } })
    }

    // Onboarding lockdown: subscription is active but onboarding not complete
    if (authStore.isOnboardingLocked) {
      if (router.app && router.app.$dekatoast) {
        router.app.$dekatoast.warning(
          'Selesaikan proses onboarding terlebih dahulu.'
        )
      }
      return next({ name: 'workspace', query: { tab: 'onboarding' } })
    }

    // Feature gate: block direct URL access to routes requiring a subscription feature
    const requiredFeature = to.meta && to.meta.feature
    if (requiredFeature) {
      const subscriptionStore = useSubscriptionStore()
      const roles = (authStore.userData && authStore.userData.roles) || []
      const isSuperadmin = roles.includes('superadmin')
      // Only gate if subscription is loaded; skip check while still loading on boot
      if (!isSuperadmin && subscriptionStore.currentSubscription) {
        if (!subscriptionStore.activeFeatures.includes(requiredFeature)) {
          if (router.app && router.app.$dekatoast) {
            router.app.$dekatoast.warning(
              'Fitur ini tidak tersedia pada paket langganan Anda.'
            )
          }
          return next({ name: 'workspace', query: { tab: 'berlangganan' } })
        }
      }
    }
  }

  return next()
})

// ? For splash screen
// Remove afterEach hook if you are not using splash screen
router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
})

// Handle chunk loading errors during navigation
router.onError((error) => {
  if (!handleChunkError(error)) {
    // If not a chunk error, log it for debugging
    console.error('Router error:', error)
  }
})

export default router
